// Долна лента с 4 таба — постоянно видима навигация, различна по роля.
// Заменя хамбургер менюто като основен начин за движение. Показва се само
// на „кореновите" екрани; при влизане в детайл (форма, чат) се скрива.

import React, { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { navigationRef } from './drawer';
import { COLORS } from './ui';
import { t } from './i18n';
import { BellIcon, BuildingIcon, ChatIcon, CoinsIcon, HomeIcon, MoreIcon, UserIcon, WrenchIcon } from './nav-icons';

type IconCmp = (p: { color: string; size?: number }) => React.ReactElement;
type Tab = { screen: string; label: string; Icon: IconCmp };

// Табовете по роля. „Още" води към екран със второстепенните инструменти.
// Функция, за да се преведат етикетите при смяна на езика.
function getTabs(): Record<string, Tab[]> {
  const TABS: Record<string, Tab[]> = {
    LANDLORD: [
      { screen: 'Dashboard', label: t('Начало'), Icon: HomeIcon },
      { screen: 'Apartments', label: t('Имоти'), Icon: BuildingIcon },
      { screen: 'Conversations', label: t('Съобщения'), Icon: ChatIcon },
      { screen: 'More', label: t('Още'), Icon: MoreIcon },
    ],
    TENANT: [
      { screen: 'TenantHome', label: t('Моят дом'), Icon: HomeIcon },
      { screen: 'Issues', label: t('Ремонти'), Icon: WrenchIcon },
      { screen: 'Conversations', label: t('Съобщения'), Icon: ChatIcon },
      { screen: 'More', label: t('Още'), Icon: MoreIcon },
    ],
    HANDYMAN: [
      { screen: 'HandymanRequests', label: t('Поръчки'), Icon: WrenchIcon },
      { screen: 'Notifications', label: t('Известия'), Icon: BellIcon },
      { screen: 'Profile', label: t('Профил'), Icon: UserIcon },
    ],
    OWNER: [
      { screen: 'OwnerApartments', label: t('Имоти'), Icon: BuildingIcon },
      { screen: 'Issues', label: t('Ремонти'), Icon: WrenchIcon },
      { screen: 'Profile', label: t('Профил'), Icon: UserIcon },
    ],
    CREATOR: [
      { screen: 'CreatorHome', label: t('Начало'), Icon: HomeIcon },
      { screen: 'CreatorMoney', label: t('Пари'), Icon: CoinsIcon },
      { screen: 'CreatorInvites', label: t('Покани'), Icon: ChatIcon },
      { screen: 'Profile', label: t('Профил'), Icon: UserIcon },
    ],
    // Обитател (живее в имота, не е страна по наема): само дом + известия + профил
    RESIDENT: [
      { screen: 'ResidentHome', label: t('Моят дом'), Icon: HomeIcon },
      { screen: 'Notifications', label: t('Известия'), Icon: BellIcon },
      { screen: 'Profile', label: t('Профил'), Icon: UserIcon },
    ],
  };
  TABS.AGENCY = TABS.LANDLORD;
  return TABS;
}

// Лентата е ЗАКАЧЕНА на ВСИЧКИ екрани — включително формите (протокол,
// договор, нова сметка, оценка). Скрива се само там, където няма долно
// място (входните екрани без потребител се обработват отделно).
const HIDE_ON = new Set<string>([]);

// Кой таб да свети на детайлен/разглеждащ екран (той не е коренов на таб,
// но лентата стои закачена и трябва да сочи раздела, в който сме).
const OWNED_BY: Record<string, string> = {
  ApartmentDetail: 'Apartments',
  OwnerApartmentDetail: 'OwnerApartments',
  Documents: 'More',
  Invoices: 'More',
  Listings: 'More',
  Subscription: 'More',
  Reputation: 'More',
  Notifications: 'More', // при наемодател/наемател известията са в „Още"
  Profile: 'More',
  Issues: 'More', // при наемодател нередностите са в „Още"; при наемател са таб (има свой запис долу)
  NewIssue: 'ResidentHome', // при обитател формата се отваря от „Моят дом“
  Residents: 'Apartments',
};
// Роли, при които даден екран е СОБСТВЕН таб → не пренасочваме highlight-а
function activeTabFor(route: string, tabs: Tab[]): string | undefined {
  if (tabs.some((t) => t.screen === route)) return route; // самият таб
  const owner = OWNED_BY[route];
  if (owner && tabs.some((t) => t.screen === owner)) return owner;
  return undefined; // непознат екран — лентата стои, но без активен таб
}

// текущото име на маршрута — за да знаем кой таб е активен и кога да се скрием
function useRouteName(): string | undefined {
  const [name, setName] = useState<string | undefined>(() =>
    navigationRef.isReady() ? navigationRef.getCurrentRoute()?.name : undefined
  );
  useEffect(() => {
    if (!navigationRef.isReady()) return;
    const unsub = navigationRef.addListener('state', () => setName(navigationRef.getCurrentRoute()?.name));
    setName(navigationRef.getCurrentRoute()?.name);
    return unsub;
  }, []);
  return name;
}

export function BottomNav({ role }: { role: string }) {
  const insets = useSafeAreaInsets();
  const route = useRouteName();
  const tabs = getTabs()[role];
  if (!tabs) return null;

  // Скрива се само на екраните за концентрирана задача; иначе стои закачена.
  if (!route || HIDE_ON.has(route)) return null;
  const active = activeTabFor(route, tabs);

  return (
    <View
      style={{
        flexDirection: 'row',
        backgroundColor: COLORS.card,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        paddingBottom: Math.max(insets.bottom, 8),
        paddingTop: 8,
      }}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.screen;
        const color = isActive ? COLORS.primary : COLORS.muted;
        return (
          <Pressable
            key={tab.screen}
            onPress={() => {
              if (navigationRef.isReady() && route !== tab.screen) navigationRef.navigate(tab.screen as never);
            }}
            style={{ flex: 1, alignItems: 'center', gap: 3, paddingVertical: 2 }}
            hitSlop={8}
          >
            <tab.Icon color={color} size={23} />
            <Text style={{ fontSize: 11, fontWeight: isActive ? '700' : '500', color }}>{tab.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}
